import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgDesktop from '../images/background-desktop.jpg';
import bgMobile from '../images/background.png';

const isDesktop = () => window.matchMedia('(min-width: 600px)').matches;

function AuthIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'shield':    return <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
        case 'user':      return <svg {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
        case 'mail':      return <svg {...p}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
        case 'lock':      return <svg {...p}><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
        case 'eye':       return <svg {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
        case 'eye-off':   return <svg {...p}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;
        case 'arrow-right': return <svg {...p}><path d="M5 12h14M12 5l7 7-7 7"/></svg>;
        default: return null;
    }
}

export default function Register() {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        username: '', email: '', confirmEmail: '', password: '', confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [ready, setReady] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors({});

        if (formData.username.length < 2 || formData.username.length > 15) {
            setErrors(err => ({ ...err, username: t('errors.username_length') }));
            return;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setErrors(err => ({ ...err, email: t('errors.email_format') }));
            return;
        }
        if (formData.email !== formData.confirmEmail) {
            setErrors(err => ({ ...err, confirmEmail: t('errors.email_match') }));
            return;
        }
        if (!/^.{8,}$/.test(formData.password)) {
            setErrors(err => ({ ...err, password: t('errors.password_length') }));
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setErrors(err => ({ ...err, confirmPassword: t('errors.password_match') }));
            return;
        }

        setLoading(true);
        const data = new FormData();
        data.append('username', formData.username);
        data.append('email', formData.email);
        data.append('password', formData.password);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${process.env.REACT_APP_API_URL}/users`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4) {
                setLoading(false);
                if (this.status === 200) {
                    const user = JSON.parse(this.responseText);
                    localStorage.setItem('userLogged', JSON.stringify({
                        username: user.username, profile: user.profile,
                        wines: user.wines, average: user.average, id: user.id
                    }));
                    navigate('/edit-user');
                } else if (this.status === 409) {
                    const error = JSON.parse(this.responseText);
                    setErrors(err => ({ ...err, email: error.error }));
                } else {
                    setErrors(err => ({ ...err, general: t('errors.connection') }));
                }
            }
        };
        xhr.send(data);
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(f => ({ ...f, [id]: value }));
    };

    const bg = isDesktop() ? bgDesktop : bgMobile;

    return (
        <div className={`auth-stage${ready ? ' auth-ready' : ''}`}>
            <div className="auth-bg" style={{ backgroundImage: `url(${bg})` }} />
            <div className="auth-vignette" />
            <div className="auth-grain" />

            <div className="auth-card auth-card-register">
                <div className="auth-brand">
                    <AuthIcon name="shield" size={14} />
                    <span className="auth-brand-name">{t('common.wine_police')}</span>
                </div>

                <h1 className="auth-heading">{t('register.title')}</h1>

                <form className="auth-form" onSubmit={handleSubmit}>
                    <div className={`auth-field${errors.username ? ' auth-field-err' : ''}`}>
                        <input
                            className="auth-input"
                            type="text"
                            id="username"
                            value={formData.username}
                            onChange={handleChange}
                            placeholder=" "
                            autoComplete="username"
                        />
                        <label className="auth-label" htmlFor="username"><AuthIcon name="user" size={11} />{t('common.username')}</label>
                    </div>
                    {errors.username && <p className="auth-error">{errors.username}</p>}

                    <div className="auth-field-pair">
                        <div className={`auth-field${errors.email ? ' auth-field-err' : ''}`}>
                            <input
                                className="auth-input"
                                type="email"
                                id="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder=" "
                                autoComplete="email"
                            />
                            <label className="auth-label" htmlFor="email"><AuthIcon name="mail" size={11} />{t('common.email')}</label>
                        </div>
                        <div className={`auth-field${errors.confirmEmail ? ' auth-field-err' : ''}`}>
                            <input
                                className="auth-input"
                                type="email"
                                id="confirmEmail"
                                value={formData.confirmEmail}
                                onChange={handleChange}
                                placeholder=" "
                                autoComplete="email"
                            />
                            <label className="auth-label" htmlFor="confirmEmail"><AuthIcon name="mail" size={11} />{t('register.label_confirm_email')}</label>
                        </div>
                    </div>
                    {errors.email && <p className="auth-error">{errors.email}</p>}
                    {errors.confirmEmail && <p className="auth-error">{errors.confirmEmail}</p>}

                    <div className="auth-field-pair">
                        <div className={`auth-field auth-field-pass${errors.password ? ' auth-field-err' : ''}`}>
                            <input
                                className="auth-input"
                                type={showPass ? 'text' : 'password'}
                                id="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder=" "
                                autoComplete="new-password"
                            />
                            <label className="auth-label" htmlFor="password"><AuthIcon name="lock" size={11} />{t('common.password')}</label>
                            <button type="button" className="auth-eye" onClick={() => setShowPass(s => !s)} tabIndex={-1}>
                                <AuthIcon name={showPass ? 'eye-off' : 'eye'} size={14} />
                            </button>
                        </div>
                        <div className={`auth-field auth-field-pass${errors.confirmPassword ? ' auth-field-err' : ''}`}>
                            <input
                                className="auth-input"
                                type={showConfirm ? 'text' : 'password'}
                                id="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder=" "
                                autoComplete="new-password"
                            />
                            <label className="auth-label" htmlFor="confirmPassword"><AuthIcon name="lock" size={11} />{t('register.label_confirm_password')}</label>
                            <button type="button" className="auth-eye" onClick={() => setShowConfirm(s => !s)} tabIndex={-1}>
                                <AuthIcon name={showConfirm ? 'eye-off' : 'eye'} size={14} />
                            </button>
                        </div>
                    </div>
                    {errors.password && <p className="auth-error">{errors.password}</p>}
                    {errors.confirmPassword && <p className="auth-error">{errors.confirmPassword}</p>}

                    {errors.general && (
                        <p className="auth-error auth-error-block">{errors.general}</p>
                    )}

                    <button className="auth-submit" type="submit" disabled={loading}>
                        {loading ? <span className="auth-spinner" /> : <>{t('common.send')}<AuthIcon name="arrow-right" size={15} /></>}
                    </button>
                </form>

                <div className="auth-links">
                    <button className="auth-link" onClick={() => navigate('/login')}>
                        {t('register.back_to_login')}
                    </button>
                </div>
            </div>
        </div>
    );
}
